/**
 * カレンダーの表示範囲（月 / 週 / 日）の計算。
 *
 * 外部ライブラリは使わない。日付計算だけで組めるうえ、依存を増やさず見た目を完全に
 * 制御できるため（cortex の catalog にカレンダーライブラリは無い）。
 *
 * 週の開始は日曜。ふだん使っている Google カレンダーと同じ並びにして、見比べたときに迷わないようにする。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business カレンダーの表示単位（月/週/日）ごとに、並べる日付と見出しを計算する。
 * @graph-connects none
 */
import { MONTHLY_FMT } from '@/fmt/fmt-variant';
import type { Task } from './task';

/**
 * カレンダーの表示単位。
 */
export type CalendarUnit = 'month' | 'week' | 'day';

/**
 * カレンダーの 1 マス。
 */
export interface CalendarCell {
  /** `YYYY-MM-DD` */
  date: string;
  /** 日（1-31） */
  day: number;
  /** 月表示で、基準月に属するか。前後の月のはみ出しは false */
  inFocus: boolean;
}

/** 週の見出し。週の開始は日曜（Google カレンダーに合わせる） */
/** @graph-connects none */
export const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const;

/** @graph-connects none */
function toIsoDate(utc: Date): string {
  return utc.toISOString().slice(0, 10);
}

/** @graph-connects none */
function utcOf(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
}

/**
 * 今日（日本時間）を返す。
 *
 * 画面を見るのは日本にいる人だけだが、端末のタイムゾーン設定に引きずられると
 * 日付が 1 日ずれる（月をまたぐと「どの週か」まで変わる）。見る人の設定に関係なく
 * 同じ日を指すよう、日本時間で組み立てる。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 今日の日付を日本時間で返す。画面の初期表示を今日に合わせるために使う
 * @graph-connects none
 */
export function today(now: Date = new Date()): string {
  // en-CA は `YYYY-MM-DD` で返る
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business いまの時刻を日本時間の 0 時からの分で返す。カレンダーに現在時刻の線を引くために使う
 * @graph-connects none
 */
export function nowMinutes(now: Date = new Date()): number {
  const [hour, minute] = now
    .toLocaleTimeString('en-GB', { timeZone: 'Asia/Tokyo', hour12: false })
    .split(':')
    .map(Number);
  return ((hour ?? 0) % 24) * 60 + (minute ?? 0);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 週番号を返す。Google カレンダーと同じく日曜始まりで、1 月 1 日を含む週を第 1 週と数える
 * @graph-connects startOfWeek [calls] 週の始まりの日曜を求める
 */
export function weekNumber(iso: string): number {
  const first = utcOf(startOfWeek(`${iso.slice(0, 4)}-01-01`));
  const week = utcOf(startOfWeek(iso));
  // 年末の週は翌年の 1 月 1 日を含むことがある。その場合は翌年の第 1 週として数える
  const nextYearFirst = utcOf(startOfWeek(`${String(Number(iso.slice(0, 4)) + 1)}-01-01`));
  if (week.getTime() >= nextYearFirst.getTime()) {
    return 1;
  }
  return Math.round((week.getTime() - first.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
}

/**
 * いま締めている月（`YYYY-MM`）を返す。
 *
 * 月次経理は翌月に締めるので、10 月に開いたら 9 月分を見ていることが多い。
 * 画面の既定値をここに合わせる。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business いま締めている月を返す。今日の前月
 * @graph-connects today [calls] 今日の日付を取る
 */
export function closingMonth(now: Date = new Date()): string {
  const [year, month] = today(now).split('-').map(Number);
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 2, 1));
  return toIsoDate(shifted).slice(0, 7);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 基準日を含む週の日曜を返す。
 * @graph-connects none
 */
export function startOfWeek(iso: string): string {
  const date = utcOf(iso);
  // Google カレンダーと同じ日曜始まり。getUTCDay() は日曜 0 なので、そのまま戻せばよい
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return toIsoDate(date);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business `YYYY-MM` を「2026年9月」の表記にする。ブラウザ既定の月入力は英語表記になるため、画面側で作る。
 * @graph-connects none
 */
export function formatYearMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-');
  return `${String(year)}年${String(Number(month))}月`;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 日付の曜日表記（月〜日）を返す。
 * @graph-connects none
 */
export function weekdayLabel(iso: string): string {
  return WEEKDAY_LABELS[utcOf(iso).getUTCDay()] ?? '';
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 基準日を日数で動かす。月・年の繰り上がりを扱う。
 * @graph-connects none
 */
export function shiftDays(iso: string, delta: number): string {
  const date = utcOf(iso);
  date.setUTCDate(date.getUTCDate() + delta);
  return toIsoDate(date);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 基準日を月数で動かす。月末日が無い月では末日に丸める（3/31 の 1 月前は 2/28）。
 * @graph-connects none
 */
export function shiftMonths(iso: string, delta: number): string {
  const date = utcOf(iso);
  const day = date.getUTCDate();
  // 日を 1 に寄せてから月を動かし、最後に元の日へ戻す（丸め込みで翌月へ溢れないように）
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + delta);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return toIsoDate(date);
}

/**
 * カレンダー取り込みの対象期間。
 *
 * 期間を選ばせない。当日から 1 ヶ月先まで見えていれば予定の把握に困らず、過ぎた予定を
 * 取り込んでも手は打てない。選択肢を置くと毎回考える手間が増えるだけなので固定する。
 * @graph-connects none
 */
export const IMPORT_RANGE_FUTURE_MONTHS = 1;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 取り込み対象の期間（当日から 1 ヶ月後まで）を返す。
 * @graph-connects shiftMonths [calls] 終了日を求める
 */
export function importRange(baseDate: string): { from: string; to: string } {
  return { from: baseDate, to: shiftMonths(baseDate, IMPORT_RANGE_FUTURE_MONTHS) };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 表示単位に応じて基準日を 1 つ前/後へ動かす。
 * @graph-connects shiftDays [calls] 日・週の移動
 * @graph-connects shiftMonths [calls] 月の移動
 */
export function shiftByUnit(iso: string, unit: CalendarUnit, delta: number): string {
  if (unit === 'month') {
    return shiftMonths(iso, delta);
  }
  return shiftDays(iso, unit === 'week' ? delta * 7 : delta);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 表示単位に応じて並べる日付を返す。月は 7 の倍数マス、週は 7 マス、日は 1 マス。
 * @graph-connects startOfWeek [calls] 週表示の開始日を求める
 */
export function buildCalendarCells(iso: string, unit: CalendarUnit): CalendarCell[] {
  if (unit === 'day') {
    const date = utcOf(iso);
    return [{ date: iso, day: date.getUTCDate(), inFocus: true }];
  }

  if (unit === 'week') {
    const first = startOfWeek(iso);
    return Array.from({ length: 7 }, (_, index) => {
      const date = shiftDays(first, index);
      return { date, day: utcOf(date).getUTCDate(), inFocus: true };
    });
  }

  const base = utcOf(iso);
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  const gridStart = startOfWeek(toIsoDate(firstOfMonth));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const leading = firstOfMonth.getUTCDay();
  // 行を 7 マスで揃える
  const total = Math.ceil((leading + daysInMonth) / 7) * 7;

  return Array.from({ length: total }, (_, index) => {
    const date = shiftDays(gridStart, index);
    const cellDate = utcOf(date);
    return {
      date,
      day: cellDate.getUTCDate(),
      inFocus: cellDate.getUTCMonth() === month,
    };
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 表示中の範囲の見出しを返す。Google カレンダーに合わせ、月と週は「2026年10月」（週が月をまたぐときは「2026年9月〜10月」）、日は「2026年10月14日（水）」。
 * @graph-connects buildCalendarCells [calls] 範囲の両端を求める
 */
export function formatRangeLabel(iso: string, unit: CalendarUnit): string {
  const base = utcOf(iso);
  const year = base.getUTCFullYear();

  if (unit === 'day') {
    const weekday = WEEKDAY_LABELS[base.getUTCDay()] ?? '';
    return `${String(year)}年${String(base.getUTCMonth() + 1)}月${String(base.getUTCDate())}日（${weekday}）`;
  }

  if (unit === 'month') {
    return `${String(year)}年${String(base.getUTCMonth() + 1)}月`;
  }

  const cells = buildCalendarCells(iso, unit);
  const first = utcOf(cells[0]?.date ?? iso);
  const last = utcOf(cells[cells.length - 1]?.date ?? iso);
  if (first.getUTCFullYear() !== last.getUTCFullYear()) {
    return `${String(first.getUTCFullYear())}年${String(first.getUTCMonth() + 1)}月〜${String(last.getUTCFullYear())}年${String(last.getUTCMonth() + 1)}月`;
  }
  if (first.getUTCMonth() !== last.getUTCMonth()) {
    return `${String(first.getUTCFullYear())}年${String(first.getUTCMonth() + 1)}月〜${String(last.getUTCMonth() + 1)}月`;
  }
  return `${String(first.getUTCFullYear())}年${String(first.getUTCMonth() + 1)}月`;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business その FMT のタスクがある月のうち、いちばん新しい月（`YYYY-MM`）を返す。1 件も無ければ undefined。進捗を開いたとき最初に見せる月に使う
 * @graph-connects none
 */
export function latestTaskMonth(
  tasks: readonly Task[],
  kind: string = MONTHLY_FMT.kind,
): string | undefined {
  return tasks
    .filter((task) => task.kind === kind)
    .map((task) => task.targetMonth ?? task.dueDate.slice(0, 7))
    .reduce<string | undefined>(
      (latest, month) => (latest === undefined || month > latest ? month : latest),
      undefined,
    );
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 進捗を開いたとき最初に見せる月。タスクがある最新の月で、1 件も無ければ締めの対象月（前月）
 * @graph-connects latestTaskMonth [calls] タスクがある最新の月を引く
 * @graph-connects closingMonth [calls] タスクが無いときの締めの対象月を引く
 */
export function defaultProgressMonth(
  tasks: readonly Task[],
  kind: string = MONTHLY_FMT.kind,
  now: Date = new Date(),
): string {
  return latestTaskMonth(tasks, kind) ?? closingMonth(now);
}
